import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Reveal, { EASE } from "./Reveal";
import ChapterArt from "./ChapterArt";

export default function Chapter({
  id,
  index,
  title,
  subtitle,
  body,
  process,
  quote,
  pillars,
  flip,
}) {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);

  return (
    <section id={id} data-testid={id} className="relative px-6 py-28 sm:px-[8vw] sm:py-40">
      <span className="f-mono pointer-events-none absolute right-4 top-8 select-none text-[26vw] leading-none text-amber-500/10 sm:right-[6vw] sm:text-[11rem]">
        {index}
      </span>

      <p className="f-mono mb-6 text-[11px] tracking-[0.35em] text-[#B8860B]">
        CHAPTER {index}
      </p>
      <Reveal>
        <h2 className="f-display text-4xl font-light tracking-tight text-slate-900 sm:text-6xl lg:text-7xl">
          {title}
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="f-display mt-3 text-xl italic text-[#B8860B] sm:text-2xl">{subtitle}</p>
      </Reveal>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={{ duration: 1, ease: EASE }}
        className="mt-16 grid items-start gap-12 md:grid-cols-12"
      >
        <div className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}>
          <div
            ref={imgRef}
            className="relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/25 bg-white"
          >
            <motion.div style={{ y }} className="absolute -top-[10%] left-0 h-[120%] w-full">
              <ChapterArt variant={index} />
            </motion.div>
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/40" />
          </div>
          <p className="f-mono mt-3 text-[10px] tracking-[0.3em] text-slate-400">
            FIG. {index} — {title.toUpperCase()}
          </p>
        </div>

        <div className={`md:col-span-6 ${flip ? "md:order-1" : "md:col-start-7"}`}>
          {body.map((p, i) => (
            <p
              key={i}
              className="mb-5 text-base font-light leading-relaxed text-slate-600 sm:text-lg"
            >
              {p}
            </p>
          ))}
          {process && (
            <div className="mt-9 flex flex-wrap items-center gap-3">
              {process.map((s, i) => (
                <span key={s} className="flex items-center gap-3">
                  <span className="f-mono border border-[#D4AF37]/40 px-3 py-2 text-[11px] tracking-[0.25em] text-slate-800">
                    {s}
                  </span>
                  {i < process.length - 1 && <ArrowRight className="h-4 w-4 text-[#D4AF37]" />}
                </span>
              ))}
            </div>
          )}
          {pillars && (
            <div className="mt-9 space-y-3">
              {pillars.map((p) => (
                <p
                  key={p}
                  className="f-mono border-l-2 border-[#D4AF37] pl-4 text-[11px] tracking-[0.3em] text-slate-800"
                >
                  {p}
                </p>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      <Reveal delay={0.1} className="mt-20">
        <blockquote className="f-display max-w-3xl text-2xl font-light leading-snug text-slate-900 sm:text-4xl">
          <span className="text-[#D4AF37]">&ldquo;</span>
          {quote}
          <span className="text-[#D4AF37]">&rdquo;</span>
        </blockquote>
      </Reveal>
    </section>
  );
}
